import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import AuthService from "../services/authService";
import Logout from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import IntegrationInstructionsOutlinedIcon from "@mui/icons-material/IntegrationInstructionsOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import { Link, useNavigate } from "react-router-dom";
import { Box, Typography, Chip } from "@mui/material";
import { useState, useEffect } from "react";

export default function MenuUsuario() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [userName, setUserName] = useState(null);
  const [userRol, setUserRol] = useState(null);

  useEffect(() => {
    setUserName(sessionStorage.getItem("username"));
    setUserRol(sessionStorage.getItem("rol"));
  }, []);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigate = useNavigate();

  const handleLogout = () => {
    AuthService.singOut();
    navigate("/login");
  };

  const getFirstLetter = (name) => {
    return name ? name.charAt(0).toUpperCase() : "?";
  };

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const formatRol = (rol) => {
    if (!rol) return "";
    return rol.charAt(0).toUpperCase() + rol.slice(1).toLowerCase();
  };

  const menuItemSx = {
    py: 1.2,
    px: 2,
    borderRadius: 1,
    mx: 0.5,
    "&:hover": { bgcolor: "action.hover" },
  };

  return (
    <React.Fragment>
      <Box
        display="flex"
        alignItems="center"
        onClick={handleClick}
        aria-controls={open ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          cursor: "pointer",
          gap: 1.2,
          px: 1,
          py: 0.5,
          borderRadius: "10px",
          transition: "background-color 0.2s",
          "&:hover": { backgroundColor: "#f5f5f5" },
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            fontSize: "0.9rem",
            fontWeight: 700,
            backgroundColor: "#ffffff",
            color: (theme) => theme.palette.secondary.main,
          }}
        >
          {getFirstLetter(userName)}
        </Avatar>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#ffffff",
            fontSize: "0.9rem",
          }}
        >
          {capitalize(userName) || "Cargando..."}
        </Typography>
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 4px 20px rgba(0,0,0,0.12))",
            mt: 1.5,
            borderRadius: "12px",
            minWidth: 240,
            border: "1px solid",
            borderColor: "grey.100",
            py: 0.5,
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
              borderLeft: "1px solid",
              borderTop: "1px solid",
              borderColor: "grey.100",
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* User info header */}
        <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                fontSize: "1rem",
                fontWeight: 700,
                bgcolor: (theme) => theme.palette.primary.main,
              }}
            >
              {getFirstLetter(userName)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {capitalize(userName) || "Usuario"}
              </Typography>
              {userRol && (
                <Chip
                  label={formatRol(userRol)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    bgcolor: "primary.50",
                    color: "primary.main",
                    mt: 0.3,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mx: 1, mb: 0.5 }} />

        {/* Navigation items. Perfil (cuenta/depósito de la empresa) es solo-owner. */}
        {AuthService.isOwner() && (
          <Link to="/profile" style={{ textDecoration: "none", color: "inherit" }}>
            <MenuItem onClick={handleClose} sx={menuItemSx}>
              <ListItemIcon>
                <PersonOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Perfil"
                secondary="Gestiona tu cuenta"
                primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: "0.72rem" }}
              />
            </MenuItem>
          </Link>
        )}
        {/* Planes: gestión del plan/suscripción de la empresa. Solo-owner. */}
        {AuthService.isOwner() && (
          <Link to="/plans" style={{ textDecoration: "none", color: "inherit" }}>
            <MenuItem onClick={handleClose} sx={menuItemSx}>
              <ListItemIcon>
                <CreditCardOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Planes"
                secondary="Tu plan y consumo"
                primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: "0.72rem" }}
              />
            </MenuItem>
          </Link>
        )}
        {/* Colaboradores: quien tiene permiso de Usuarios (owner siempre) y también
            el super-admin (id 1), que tiene acceso a todo. */}
        {AuthService.canAccessTab("user") && (
          <Link to="/collaborators" style={{ textDecoration: "none", color: "inherit" }}>
            <MenuItem onClick={handleClose} sx={menuItemSx}>
              <ListItemIcon>
                <PeopleOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Colaboradores"
                secondary="Administra tu equipo"
                primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: "0.72rem" }}
              />
            </MenuItem>
          </Link>
        )}
        {/* Integraciones (servicios externos de la empresa) es solo-owner. */}
        {AuthService.isOwner() && (
          <Link to="/integrations" style={{ textDecoration: "none", color: "inherit" }}>
            <MenuItem onClick={handleClose} sx={menuItemSx}>
              <ListItemIcon>
                <IntegrationInstructionsOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Integraciones"
                secondary="Conecta servicios externos"
                primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: "0.72rem" }}
              />
            </MenuItem>
          </Link>
        )}

        <Divider sx={{ mx: 1, my: 0.5 }} />

        <MenuItem
          onClick={handleLogout}
          sx={{
            ...menuItemSx,
            color: "error.main",
            "&:hover": { bgcolor: "error.50" },
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <ListItemText
            primary="Cerrar sesion"
            primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
          />
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
}
